import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { crateUserPackage, createTransactionForPackage, caculateUserPackage } from '@/api/Admin/adminPackage';
import { fetchAllUser } from '@/api/Admin/User';
import { fetchPackageAdminList, userInvestmentPackageStats } from '@/api/Admin/userPackage';
import { ListUserAPI } from '@/api/Admin/type';
import TrashIcon from '@/components/Icon/TrashIcon';
import PlusIcon from '@/components/Icon/PlusIcon';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface TransactionItem {
  id?: string; // To track if created
  amount: string;
  transaction_type: string;
  transaction_created_at: string;
  loading?: boolean;
  completed?: boolean;
  statusMessage?: {
    type: 'success' | 'error';
    text: string;
  } | null;
}

const RegisterPackagePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const userIdFromQuery = searchParams.get('user_id');

  const [loadingStep1, setLoadingStep1] = useState(false);
  const [loadingStep2, setLoadingStep2] = useState(false);
  const [loadingStep3, setLoadingStep3] = useState(false);
  
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  const [fetchingPlans, setFetchingPlans] = useState(false);
  const [plans, setPlans] = useState<any[]>([]);
  
  // Step 1 Results (Chained)
  const [userPackageId, setUserPackageId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  // Step 2 Selection
  const [step2UserId, setStep2UserId] = useState<string>(userIdFromQuery || '');
  const [step2UserPackageId, setStep2UserPackageId] = useState<string>('');
  const [step2UserPackages, setStep2UserPackages] = useState<any[]>([]);
  const [loadingPackagesStep2, setLoadingPackagesStep2] = useState(false);

  // Step 3 Selection
  const [step3UserId, setStep3UserId] = useState<string>(userIdFromQuery || '');
  const [step3UserPackageId, setStep3UserPackageId] = useState<string>('');
  const [step3UserName, setStep3UserName] = useState<string>(''); // Needed for calculations
  const [step3UserPackages, setStep3UserPackages] = useState<any[]>([]);
  const [loadingPackagesStep3, setLoadingPackagesStep3] = useState(false);

  const [formData, setFormData] = useState({
    user_id: userIdFromQuery || '',
    package_id: '',
    started_at: dayjs().format('YYYY-MM-DDTHH:mm'),
    transactions: [
      {
        amount: '',
        transaction_type: 'investment',
        transaction_created_at: dayjs().format('YYYY-MM-DDTHH:mm'),
        loading: false,
        completed: false,
        statusMessage: null,
      }
    ] as TransactionItem[]
  });

  useEffect(() => {
    const loadUsers = async () => {
      setFetchingUsers(true);
      try {
        const res: ListUserAPI = await fetchAllUser(1, 10000);
        if (res.status) {
          setUsers(res.data.items);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setFetchingUsers(false);
      }
    };

    const loadPlans = async () => {
        setFetchingPlans(true);
        try {
            const res = await fetchPackageAdminList();
            if (res.status) {
                setPlans(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch plans:', error);
        } finally {
            setFetchingPlans(false);
        }
    };

    loadUsers();
    loadPlans();
  }, []);

  // Fetch packages when Step 2 User changes
  useEffect(() => {
    if (step2UserId) {
        loadUserPackages(step2UserId, setStep2UserPackages, setLoadingPackagesStep2);
    } else {
        setStep2UserPackages([]);
    }
  }, [step2UserId]);

  // Fetch packages when Step 3 User changes
  useEffect(() => {
    if (step3UserId) {
        loadUserPackages(step3UserId, setStep3UserPackages, setLoadingPackagesStep3);
    } else {
        setStep3UserPackages([]);
    }
  }, [step3UserId]);

  const loadUserPackages = async (uid: string, setter: (pkgs: any[]) => void, loader: (l: boolean) => void) => {
    loader(true);
    try {
        const res = await userInvestmentPackageStats(uid);
        if (res.status && res.data?.investment?.history) {
            // Need to handle if history is a paginated object or array
            const history = res.data.investment.history;
            const pkgs = Array.isArray(history) ? history : ((history as any).data || []);
            setter(pkgs);
        } else {
            setter([]);
        }
    } catch (error) {
        console.error('Failed to load user packages:', error);
        setter([]);
    } finally {
        loader(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'package_id') {
      const selectedPlan = plans.find(p => p.id === Number(value));
      if (selectedPlan) {
        const updatedTransactions = [...formData.transactions];
        if (updatedTransactions.length > 0 && !updatedTransactions[0].completed) {
          updatedTransactions[0].amount = String(selectedPlan.price);
          setFormData(prev => ({ ...prev, transactions: updatedTransactions }));
        }
      }
    }
  };

  const handleTransactionChange = (index: number, field: keyof TransactionItem, value: any) => {
    setFormData(prev => {
      const updatedTransactions = [...prev.transactions];
      updatedTransactions[index] = { ...updatedTransactions[index], [field]: value };
      return { ...prev, transactions: updatedTransactions };
    });
  };

  const addTransaction = () => {
    setFormData(prev => ({
      ...prev,
      transactions: [
        ...prev.transactions,
        {
          amount: '',
          transaction_type: 'investment',
          transaction_created_at: dayjs().format('YYYY-MM-DDTHH:mm'),
          loading: false,
          completed: false,
          statusMessage: null,
        }
      ]
    }));
  };

  const removeTransaction = (index: number) => {
    if (formData.transactions.length > 1) {
      const updatedTransactions = formData.transactions.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, transactions: updatedTransactions }));
    }
  };

  const executeStep1 = async () => {
    if (!formData.user_id || !formData.package_id) {
      toast.error(t('registerPackage.errors.selectUserAndPackage'));
      return;
    }
    setLoadingStep1(true);
    try {
      const regInput = {
        user_id: formData.user_id,
        package_id: formData.package_id,
        started_at: dayjs(formData.started_at).format('YYYY-MM-DD HH:mm:ss'),
      };
      const regRes = await crateUserPackage(regInput);
      
      if (!regRes.data || !regRes.data.id) {
        throw new Error(t('registerPackage.errors.userPackageIdResponse'));
      }

      const newPkgId = String(regRes.data.id);
      const newUserName = regRes.data.user_info.fullname;

      setUserPackageId(newPkgId);
      setUserName(newUserName);

      // Auto-set Step 2 and 3 values for convenience
      setStep2UserId(formData.user_id);
      setStep2UserPackageId(newPkgId);
      setStep3UserId(formData.user_id);
      setStep3UserPackageId(newPkgId);
      setStep3UserName(newUserName);

      toast.success(t('registerPackage.toasts.step1Success'));
    } catch (error: any) {
      console.error('Step 1 failed:', error);
      toast.error(error.message || t('registerPackage.toasts.step1Failed'));
    } finally {
      setLoadingStep1(false);
    }
  };

  const executeStep2_Single = async (index: number) => {
    const effectivePkgId = step2UserPackageId || userPackageId;
    if (!effectivePkgId) {
      toast.error(t('registerPackage.errors.selectOrCreatePackage'));
      return;
    }

    const tx = formData.transactions[index];
    if (!tx.amount) {
      toast.error(t('registerPackage.errors.enterAmount'));
      return;
    }

    handleTransactionChange(index, 'loading', true);
    handleTransactionChange(index, 'statusMessage', null);
    
    try {
      await createTransactionForPackage({
        amount: Number(tx.amount),
        user_package_id: effectivePkgId,
        transaction_type: tx.transaction_type,
        created_at: dayjs(tx.transaction_created_at).format('YYYY-MM-DD HH:mm:ss'),
      });
      handleTransactionChange(index, 'completed', true);
      handleTransactionChange(index, 'statusMessage', { type: 'success', text: t('registerPackage.toasts.transactionCreated') });
      toast.success(t('registerPackage.toasts.transactionCreatedIndex', { index: index + 1 }));
    } catch (error: any) {
      console.error(`Step 2 (Transaction ${index + 1}) failed:`, error);
      handleTransactionChange(index, 'statusMessage', { type: 'error', text: error.message || t('registerPackage.toasts.transactionCreateFailed') });
      toast.error(error.message || t('registerPackage.toasts.transactionFailedIndex', { index: index + 1 }));
    } finally {
      handleTransactionChange(index, 'loading', false);
    }
  };

  const executeStep2_All = async () => {
    const effectivePkgId = step2UserPackageId || userPackageId;
    if (!effectivePkgId) {
      toast.error(t('registerPackage.errors.selectOrCreatePackage'));
      return;
    }

    setLoadingStep2(true);
    for (let i = 0; i < formData.transactions.length; i++) {
        if (formData.transactions[i].completed) continue;
        await executeStep2_Single(i);
    }
    setLoadingStep2(false);
  };

  const executeStep3 = async () => {
    const effectivePkgId = step3UserPackageId || userPackageId;
    let effectiveUserName = step3UserName || userName;

    if (!effectiveUserName && step3UserId) {
        const u = users.find(user => String(user.id) === step3UserId);
        if (u) effectiveUserName = u.fullname;
    }

    if (!effectivePkgId || !effectiveUserName) {
      toast.error(t('registerPackage.errors.userPackageOrNameMissing'));
      return;
    }

    setLoadingStep3(true);
    try {
      await caculateUserPackage({
        user_name: effectiveUserName,
        user_package_id: effectivePkgId,
      });
      toast.success(t('registerPackage.toasts.step3Success'));
      setTimeout(() => navigate('/admin/users'), 2000);
    } catch (error: any) {
      console.error('Step 3 failed:', error);
      toast.error(error.message || t('registerPackage.toasts.step3Failed'));
    } finally {
      setLoadingStep3(false);
    }
  };

  const labelClass = "block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1";
  const inputClass = "w-full px-3 py-2 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-neutral-100 transition duration-200 disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-neutral-900";
  const btnPrimary = "px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-md shadow-sm transition-all duration-200 flex items-center justify-center gap-2";
  const btnSecondary = "px-4 py-2 bg-gray-200 dark:bg-neutral-800 hover:bg-gray-300 dark:hover:bg-neutral-700 text-gray-800 dark:text-neutral-200 font-semibold rounded-md transition-all duration-200";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-neutral-100">{t('registerPackage.title')}</h1>
            {userPackageId && (
                <div className="flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-bold border border-green-200 dark:border-green-800">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              {t('registerPackage.recentCreation', { id: userPackageId })}
                </div>
            )}
        </div>

        {/* Step 1 Section */}
        <div className={`bg-white dark:bg-neutral-900 rounded-xl shadow-md border overflow-hidden transition-all duration-300 ${userPackageId ? 'border-green-200 dark:border-green-900/50 opacity-90' : 'border-gray-200 dark:border-neutral-800'}`}>
          <div className="px-6 py-4 border-b border-gray-100 dark:border-neutral-800 flex justify-between items-center bg-gray-50/50 dark:bg-neutral-900/50">
            <div className="flex items-center gap-3">
              <span className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${userPackageId ? 'bg-green-500 text-white' : 'bg-blue-600 text-white'}`}>1</span>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-neutral-100">{t('registerPackage.step1.title')}</h2>
            </div>
            {userPackageId && <span className="text-green-600 dark:text-green-400 text-sm font-medium">{t('registerPackage.step1.packageReady')}</span>}
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <label className={labelClass}>{t('registerPackage.step1.selectUser')}</label>
                <select
                  name="user_id"
                  value={formData.user_id}
                  onChange={handleInputChange}
                  className={inputClass}
                  disabled={fetchingUsers || !!userPackageId}
                >
                  <option value="">{t('registerPackage.step1.chooseUser')}</option>
                  {users.map(u => (
                    <option key={u.id} value={String(u.id)}>
                      {u.fullname} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>{t('registerPackage.step1.selectPackageTemplate')}</label>
                <select
                  name="package_id"
                  value={formData.package_id}
                  onChange={handleInputChange}
                  className={inputClass}
                  disabled={!!userPackageId || fetchingPlans}
                >
                  <option value="">{fetchingPlans ? t('registerPackage.step1.loadingPlans') : t('registerPackage.step1.choosePackage')}</option>
                  {plans.map(p => (
                    <option key={p.id} value={String(p.id)}>
                      {p.name} - {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(p.price))}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>{t('registerPackage.step1.subscriptionStartDate')}</label>
                <input
                  type="datetime-local"
                  name="started_at"
                  value={formData.started_at}
                  onChange={handleInputChange}
                  className={inputClass}
                  disabled={!!userPackageId}
                />
              </div>
            </div>
            
            {!userPackageId ? (
              <button 
                onClick={executeStep1} 
                disabled={loadingStep1} 
                className={btnPrimary}
              >
                {loadingStep1 ? t('registerPackage.step1.creating') : t('registerPackage.step1.registerButton')}
              </button>
            ) : (
                <button 
                    onClick={() => {setUserPackageId(null); setUserName(null);}} 
                    className="text-xs text-blue-600 hover:underline"
                >
                    {t('registerPackage.step1.createAnother')}
                </button>
            )}
          </div>
        </div>

        {/* Step 2 Section */}
        <div className={`bg-white dark:bg-neutral-900 rounded-xl shadow-md border overflow-hidden transition-all duration-300 border-gray-200 dark:border-neutral-800`}>
          <div className="px-6 py-4 border-b border-gray-100 dark:border-neutral-800 flex justify-between items-center bg-gray-50/50 dark:bg-neutral-900/50">
            <div className="flex items-center gap-3">
              <span className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold bg-blue-600 text-white`}>2</span>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-neutral-100">{t('registerPackage.step2.title')}</h2>
            </div>
            <button onClick={executeStep2_All} disabled={loadingStep2 || (!step2UserPackageId && !userPackageId)} className="text-blue-600 dark:text-blue-400 text-sm font-bold hover:underline disabled:opacity-30">
                {loadingStep2 ? t('registerPackage.step2.processingAll') : t('registerPackage.step2.runAllPending')}
            </button>
          </div>

          <div className="p-6 space-y-4">
            {/* User & Package Selection for Step 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20 mb-6">
                <div>
                  <label className={labelClass}>{t('registerPackage.step2.targetUser')}</label>
                    <select
                        value={step2UserId}
                        onChange={(e) => setStep2UserId(e.target.value)}
                        className={inputClass}
                    >
                      <option value="">{t('registerPackage.step2.chooseUser')}</option>
                        {users.map(u => (
                            <option key={u.id} value={String(u.id)}>
                                {u.fullname}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                  <label className={labelClass}>{t('registerPackage.step2.targetUserPackage')} {loadingPackagesStep2 && `(${t('registerPackage.loading')})`}</label>
                    <select
                        value={step2UserPackageId}
                        onChange={(e) => setStep2UserPackageId(e.target.value)}
                        className={inputClass}
                        disabled={loadingPackagesStep2 || !step2UserId}
                    >
                      <option value="">{t('registerPackage.step2.selectPackage')}</option>
                        {step2UserPackages.map((p: any) => (
                            <option key={p.id} value={String(p.id)}>
                          {t('registerPackage.step2.packageOption', { packageName: p.package_name, totalInvested: p.total_invested })}
                            </option>
                        ))}
                    </select>
                    {!step2UserPackageId && userPackageId && (
                      <p className="text-[10px] text-blue-600 mt-1">{t('registerPackage.step2.usingFromStep1', { id: userPackageId })}</p>
                    )}
                </div>
            </div>

            {formData.transactions.map((tx, index) => (
              <div key={index} className={`relative p-4 rounded-lg border transition-all duration-200 ${tx.completed ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/30' : 'bg-gray-50 dark:bg-neutral-800/40 border-gray-200 dark:border-neutral-800'}`}>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-sm font-bold text-gray-600 dark:text-neutral-400">{t('registerPackage.step2.transactionTitle', { index: index + 1 })}</h3>
                    <div className="flex items-center gap-2">
                    {tx.completed && <span className="text-xs font-bold text-green-600 dark:text-green-400">{t('registerPackage.step2.success')}</span>}
                        {!tx.completed && formData.transactions.length > 1 && (
                            <button
                                type="button"
                                onClick={() => removeTransaction(index)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <TrashIcon />
                            </button>
                        )}
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={labelClass}>{t('registerPackage.step2.amount')}</label>
                    <input
                      type="number"
                      value={tx.amount}
                      onChange={(e) => handleTransactionChange(index, 'amount', e.target.value)}
                      className={inputClass}
                      disabled={tx.completed}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>{t('registerPackage.step2.type')}</label>
                    <select
                      value={tx.transaction_type}
                      onChange={(e) => handleTransactionChange(index, 'transaction_type', e.target.value)}
                      className={inputClass}
                      disabled={tx.completed}
                    >
                      <option value="investment">{t('registerPackage.step2.types.investment')}</option>
                      <option value="deposit">{t('registerPackage.step2.types.deposit')}</option>
                      <option value="bonus">{t('registerPackage.step2.types.bonus')}</option>
                      <option value="other">{t('registerPackage.step2.types.other')}</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>{t('registerPackage.step2.date')}</label>
                    <input
                      type="datetime-local"
                      value={tx.transaction_created_at}
                      onChange={(e) => handleTransactionChange(index, 'transaction_created_at', e.target.value)}
                      className={inputClass}
                      disabled={tx.completed}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-2">
                    <div className="flex-1 mr-4">
                        {tx.statusMessage && (
                            <p className={`text-xs font-medium py-1 px-2 rounded ${tx.statusMessage.type === 'success' ? 'text-green-700 bg-green-100 dark:bg-green-900/20 dark:text-green-400' : 'text-red-700 bg-red-100 dark:bg-red-900/20 dark:text-red-400'}`}>
                                {tx.statusMessage.text}
                            </p>
                        )}
                    </div>

                    {!tx.completed && (
                        <button 
                            onClick={() => executeStep2_Single(index)} 
                            disabled={tx.loading || (!step2UserPackageId && !userPackageId)} 
                            className="text-xs font-bold whitespace-nowrap px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded border border-blue-200 dark:border-blue-800 hover:bg-blue-100 transition-all disabled:opacity-30"
                        >
                          {tx.loading ? t('registerPackage.step2.creating') : t('registerPackage.step2.createIndividual')}
                        </button>
                    )}
                </div>
              </div>
            ))}

            <button
                type="button"
                onClick={addTransaction}
                className="w-full flex items-center justify-center gap-2 py-2 border-2 border-dashed border-gray-300 dark:border-neutral-700 rounded-lg text-gray-500 dark:text-neutral-400 hover:border-blue-500 hover:text-blue-500 transition-all duration-200"
            >
              <PlusIcon />
              <span>{t('registerPackage.step2.addAnother')}</span>
            </button>
          </div>
        </div>

        {/* Step 3 Section */}
        <div className={`bg-white dark:bg-neutral-900 rounded-xl shadow-md border overflow-hidden transition-all duration-300 border-gray-200 dark:border-neutral-800`}>
          <div className="px-6 py-4 border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50 flex items-center gap-3">
              <span className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold bg-blue-600 text-white`}>3</span>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-neutral-100">{t('registerPackage.step3.title')}</h2>
          </div>
          <div className="p-6">
            {/* User & Package Selection for Step 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20 mb-6">
                <div>
                  <label className={labelClass}>{t('registerPackage.step3.targetUser')}</label>
                    <select
                        value={step3UserId}
                        onChange={(e) => {
                            setStep3UserId(e.target.value);
                            const u = users.find(user => String(user.id) === e.target.value);
                            setStep3UserName(u?.fullname || '');
                        }}
                        className={inputClass}
                    >
                      <option value="">{t('registerPackage.step3.chooseUser')}</option>
                        {users.map(u => (
                            <option key={u.id} value={String(u.id)}>
                                {u.fullname}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                  <label className={labelClass}>{t('registerPackage.step3.targetUserPackage')} {loadingPackagesStep3 && `(${t('registerPackage.loading')})`}</label>
                    <select
                        value={step3UserPackageId}
                        onChange={(e) => setStep3UserPackageId(e.target.value)}
                        className={inputClass}
                        disabled={loadingPackagesStep3 || !step3UserId}
                    >
                      <option value="">{t('registerPackage.step3.selectPackage')}</option>
                        {step3UserPackages.map((p: any) => (
                            <option key={p.id} value={String(p.id)}>
                          {t('registerPackage.step3.packageOption', { packageName: p.package_name, totalInvested: p.total_invested })}
                            </option>
                        ))}
                    </select>
                    {!step3UserPackageId && userPackageId && (
                      <p className="text-[10px] text-blue-600 mt-1">{t('registerPackage.step3.fallbackTo', { id: userPackageId })}</p>
                    )}
                </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-neutral-400 mb-6">
                  {t('registerPackage.step3.description')}
                  {' '}
                  <span className="font-bold text-gray-800 dark:text-neutral-200">{step3UserPackageId || userPackageId || t('registerPackage.step3.notSelected')}</span>.
                  {' '}
                  {t('registerPackage.step3.descriptionHint')}
            </p>
            <button 
              onClick={executeStep3} 
              disabled={loadingStep3 || (!step3UserPackageId && !userPackageId)} 
              className={`w-full py-3 ${btnPrimary}`}
            >
                  {loadingStep3 ? t('registerPackage.step3.calculating') : t('registerPackage.step3.executeButton')}
            </button>
          </div>
        </div>

        <div className="flex justify-center pt-4">
            <button onClick={() => navigate('/admin/users')} className={btnSecondary}>
                  {t('registerPackage.cancelAndBack')}
            </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterPackagePage;
